import { toast } from 'vue-sonner'
import { isHttpError } from '@/api/utils/http.ts'
import { useLoader, useMsgBox } from '@deepcloud/deep-ui-lib'
import {
  isNodeMimeTypeDOCX,
  isNodeMimeTypeXLSX,
} from '@deepcloud/deep-ui-utils'
import { useApi } from '@/composables/api.ts'
import { useDeepSign } from '../deep-sign'
import type {
  GenericError,
  Node,
  PathSegment,
  SearchItem,
} from '@deepcloud/api-deep-box'

export function useNodeConvertPdfAndSign() {
  const doNotShowAgainAlertConvertDocxToPdf = useLocalStorage(
    'doNotShowAgainAlertConvertDocxToPdf',
    false,
  )

  const deepSign = useDeepSign()
  const deepMsgBox = useMsgBox()
  const { t } = useI18n()
  const deepLoader = useLoader()
  const { deepBoxAPI } = useApi()

  async function doConvertWordToPdf(node: Node | PathSegment | SearchItem) {
    try {
      const res = await deepBoxAPI.value.nodesConvert.create(node.nodeId)
      return Promise.resolve(res)
    } catch (e: unknown) {
      console.error(e)

      if (isHttpError(e)) {
        const eResData = e?.response?.data as GenericError
        const responseErrorCode = eResData?.messageId
        if (responseErrorCode === 'error.file.tooBig') {
          const maxFileSize = eResData?.['maxFileSize.mb']
          toast.error(
            t('nodes.convert_to_pdf_and_sign_msg_box.errors.file_to_big_mb', {
              maxFileSize,
            }),
          )
          return Promise.reject(e)
        }
      }
      toast.error(t('error.error_occurred'))
      return Promise.reject(e)
    }
  }

  async function doConvertWordToPdfAndOpenDeepSign(node: Node | PathSegment) {
    try {
      const res = await doConvertWordToPdf(node)
      await deepSign.openDeepSignOrPreviewDocument(res?.data)
    } catch (e) {
      console.error(e)
    }
  }

  async function doConvertWordToPdfAndSign(node: Node | PathSegment) {
    if (
      !node ||
      (!isNodeMimeTypeDOCX(node.mimeType) && !isNodeMimeTypeXLSX(node.mimeType))
    )
      return

    if (!doNotShowAgainAlertConvertDocxToPdf.value) {
      const msgOptions = {
        title: t('nodes.convert_to_pdf_and_sign_msg_box.title'),
        content: t('nodes.convert_to_pdf_and_sign_msg_box.msg'),
        msgBoxType: 'checkbox',
        msgBoxTypeFormFieldProps: {
          label: t('nodes.convert_to_pdf_and_sign_msg_box.checkbox.label'),
          rules: [],
        },
        btnActions: {
          confirm: {
            text: t('buttons.proceed'),
          },
        },
      }
      const { action, checkbox } = await deepMsgBox(msgOptions)
      doNotShowAgainAlertConvertDocxToPdf.value = checkbox
      if (action === 'confirm') {
        deepLoader.show(
          t('nodes.convert_to_pdf_and_sign_msg_box.msg_converting_file_to_pdf'),
        )
        await doConvertWordToPdfAndOpenDeepSign(node)
        deepLoader.hide()
      }
    } else {
      deepLoader.show(
        t('nodes.convert_to_pdf_and_sign_msg_box.msg_converting_file_to_pdf'),
      )
      await doConvertWordToPdfAndOpenDeepSign(node)
      deepLoader.hide()
    }
  }

  return {
    doConvertWordToPdf,
    doConvertWordToPdfAndSign,
  }
}
